<template>
  <div>
    <GuidelineMenu ref="guidelineMenu" :position="menuPosition" :configuration="configuration" />
    <StationsMenu ref="stationsMenu" @update:stations="stations = $event" :position="menuPosition" :configuration="configuration" />
    <ConfirmationMenu ref="confirmationMenu" :position="menuPosition" />
    <v-card-title class="headline pb-0">
      Guideline Exceedance Report
      <span class="font-weight-light body-2">
        &nbsp;
        ⋅
        {{ configuration.guidelineStations.length }}
        {{ pluralize('guideline', configuration.guidelineStations.length)}}</span>
      <v-spacer />
      <v-text-field prepend-inner-icon="search" v-model="search" class="rounded font-weight-regular emphasize-clear" hide-details clearable @click:clear="search = ''" solo placeholder="Search guidelines or stations">
      </v-text-field>
    </v-card-title>
    <v-card-actions>
      <v-btn ref="addItem" @click="addItem()" outline small color="success">
        <v-icon>add</v-icon>
        <span class="text-">{{$t('Button.AddGuideline')}}</span>
      </v-btn>
    </v-card-actions>
    <v-card-text class="secondary--text">
      Customize which guidelines to consider when checking exceedances for imported discrete data.
    </v-card-text>
    <v-divider />
    <v-data-table :pagination.sync="pagination" :headers="headers" :search="search" :items="items" :custom-filter="customSearch">
      <template v-slot:items="{ item }">
        <td class="text-xs-left" :class="{ 'blurry-text': loading.guidelines }">
          {{ item.guidelineName || 'GUIDELINE_NAME' }}
        </td>
        <td class="text-xs-left maximize">
          <div v-if="item.stationIds.length == 0">
            All stations
          </div>
          <div v-else>
            <v-chip v-for="(stationName, index) in item.stationNames.slice(0, 10)" small :key="`stationId-${stationName}-${index}`">
              {{ stationName }}
            </v-chip>
            <v-chip small v-if="item.stationNames.length > 10">
              + {{ item.stationNames.length - 10 }} others
            </v-chip>
          </div>
        </td>
        <td class="text-xs-center no-wrap">
          <v-btn class="mx-0" @click="editStations(item)" color="info" outline small>{{$t('Button.EditStations')}}</v-btn>
          <br />
          <v-btn class="ml-0" @click="editItem(item)" icon flat color="info" small>
            <v-icon>edit</v-icon>
          </v-btn>
          <v-btn class="mr-0" @click="removeItem(item)" icon flat color="error" small>
            <v-icon>clear</v-icon>
          </v-btn>
        </td>
      </template>
    </v-data-table>
  </div>
</template>

<script>
  import {
    pluralize,
  } from '@/helpers/strings'

  import GuidelineMenu from './GuidelineMenu'
  import StationsMenu from './StationsMenu'

  const itemKey = 'guidelineId'

  import {
    customSearcher,
    getObjectValues,
  } from '@/helpers/search'

  export default {
    components: {
      GuidelineMenu,
      StationsMenu,
    },
    props: {
      configuration: Object,
    },
    data: () => ({
      search: '',
      headers: [{
        text: 'Name',
        align: 'left',
        value: 'guidelineName',
      },
      {
        text: 'Stations',
        align: 'left',
        value: 'stationNames',
      },
      {
        text: 'Actions',
        align: 'center',
        value: 'actions',
      },
      ],
      pagination: {
        rowsPerPage: -1,
      },
      menuPosition: {
        x: null,
        y: null,
      },
      stations: [],
      itemsMetadata: {},
      loading: {
        guidelines: true,
      },
    }),
    computed: {
      customSearch() {
        return customSearcher(this.itemsMetadata, itemKey)
      },
      guidelines() {
        return this.$store.getters['guidelines/guidelineOfCurrentTenant']
      },
      guidelineIndex() {
        let index = {}
        this.guidelines.forEach(guideline => index[guideline.Id] = guideline)
        return index
      },
      stationsIndex() {
        let index = {}
        this.stations.forEach(station => index[station.id] = station)
        return index
      },
      items() {
        return this.configuration.guidelineStations.map(gs => ({
          ...gs,
          guidelineName: this.guidelineName(gs.guidelineId),
          stationNames: gs.stationIds.map(stationId => this.stationsIndex[stationId] && this.stationsIndex[
            stationId].name),
        }))
      },
    },
    watch: {
      items: {
        deep: true,
        async handler() {
          await this.$nextTick()
          this.makeItemsMetadata()
        },
      },
      guidelines: {
        immediate: true,
        handler(value) {
          if (value.length == 0) {
            this.loading.guidelines = true
          } else {
            this.loading.guidelines = false
          }
        },
      },
    },
    methods: {
      pluralize,
      makeItemsMetadata() {
        let result = {}
        this.items.forEach(item => {
          result[item[itemKey]] = getObjectValues(item, 'guidelineName', 'stationNames', 'stationIds',
            'guidelineId')
        })

        this.itemsMetadata = result
      },
      guidelineName(id) {
        if (!id || !this.guidelineIndex || !this.guidelineIndex[id]) return ''
        return this.guidelineIndex[id].GuidelineName
      },
      updateMenuPosition(event) {
        this.menuPosition = {
          x: event.x,
          y: event.y,
        }
      },
      guidelineStationFromItem(item) {
        return this.configuration.guidelineStations.find(gs => gs.guidelineId == item.guidelineId)
      },
      addItem() {
        this.updateMenuPosition(event)
        this.$refs.guidelineMenu.show()
      },
      editItem(item) {
        let gs = this.guidelineStationFromItem(item)
        this.updateMenuPosition(event)
        this.$refs.guidelineMenu.show(gs)
      },
      editStations(item) {
        let gs = this.guidelineStationFromItem(item)
        this.updateMenuPosition(event)
        this.$refs.stationsMenu.show(gs)
      },
      async removeItem(item) {
        this.updateMenuPosition(event)
        let confirmationDetails = {
          title: 'Remove guideline',
          text: `Remove ${this.guidelineName(item.guidelineId)} from the list? <br/> The stations attached will be lost.`,
          confirmText: 'Remove',
          cancelText: 'Cancel',
          color: 'error',
        }

        try {
          let hasConfirmed = await this.$refs.confirmationMenu.show(confirmationDetails)
          if (hasConfirmed) {
            let index = this.configuration.guidelineStations.findIndex(gs => gs.guidelineId == item.guidelineId)
            this.configuration.guidelineStations.splice(index, 1)
          }
        } catch (error) {
          // Cancelled promise
          console.warn(error)
        }
      },
    },
  }
</script>

<style>
  .blurry-text {
    color: transparent;
    animation: blur 2.5s ease-in infinite;
  }

  @keyframes blur {
    0% {
      text-shadow: 0 0 5px rgba(0, 0, 0, 0.4);
    }

    50% {
      text-shadow: 0 0 5px rgba(0, 0, 0, 0.8);
    }

    100% {
      text-shadow: 0 0 5px rgba(0, 0, 0, 0.4);
    }
  }
</style>